import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Typography } from '@mui/material';
import { Stack } from '@mui/system';

import OptionalMetadataForm from '@/features/InventoryPage/components/OptionalMetadataForm/OptionalMetadataForm';
import useInventoryMetadataForms from '@/features/InventoryPage/hooks/useInventoryMetadataForms';
import getMetadataTransformedForm from '@/features/InventoryPage/utils/getMetadataTransformedForm';
import ModalNotification, {
  ModalNotificationProps,
} from '@/shared/components/dialogs/ModalNotification';
import Metadata from '@/shared/types/metadata/index.type';

type UpdateAtomsModalProps = {
  metadata: Metadata;
  stashedMetadata: Record<string, unknown> | null;
  errors?: string[];
  onClose: () => void;
  onStash: (details: Record<string, unknown>) => void;
  proceed: (details: Record<string, unknown>) => void;
};

const UpdateInventoryItemsDialog: FC<UpdateAtomsModalProps> = ({
  metadata,
  stashedMetadata,
  errors,
  onClose,
  onStash,
  proceed,
}) => {
  const { t } = useTranslation(['inventory', 'common']);

  const [step, setStep] = useState<'default' | 'confirmation' | 'error'>(
    'default',
  );

  const {
    optionalMetadata,
    optionalFormsList,
    handleFieldValuesChange,
    handleFieldVisibilityChange,
  } = useInventoryMetadataForms({
    metadata,
    constraint: 'ATOM',
    initialData: stashedMetadata,
  });

  useEffect(() => {
    if (errors && errors.length > 0) setStep('error');
  }, [errors]);

  const transformOptionalFormsList = () => {
    return optionalFormsList
      .flat()
      .filter(
        (item) =>
          (stashedMetadata &&
            item.field_props.name in stashedMetadata &&
            item.isVisible) ||
          item.isVisible,
      )
      .map((formField) => ({
        name: formField.field_props.name,
        value: formField.field_props.value,
        checked:
          'checked' in formField.field_props &&
          (formField.field_props.checked as boolean),
      }));
  };

  const isButtonDisabled =
    optionalFormsList.flat().filter((item) => item.isVisible).length === 0;

  const dialogConfig = {
    default: {
      title: t('inventory:updateInventory'),
      actions: [
        {
          label: t('common:stash'),
          variant: 'text',
          disabled: isButtonDisabled,
          onClick: () => onStash(getMetadataTransformedForm(optionalFormsList)),
        },
        {
          label: t('common:cancel'),
          variant: 'text',
          onClick: onClose,
        },
        {
          label: t('common:next'),
          variant: 'contained',
          color: 'primary',
          disabled: isButtonDisabled,
          onClick: () => setStep('confirmation'),
        },
      ],
    },
    confirmation: {
      title: t('inventory:reviewUpdateInventoryModal.title'),
      actions: [
        {
          label: t('common:cancel'),
          variant: 'text',
          onClick: onClose,
        },
        {
          label: t('common:back'),
          variant: 'text',
          color: 'primary',
          onClick: () => setStep('default'),
        },
        {
          label: t('common:update'),
          variant: 'contained',
          color: 'primary',
          onClick: () => proceed(getMetadataTransformedForm(optionalFormsList)),
        },
      ],
    },
    error: {
      title: t('inventory:updateInventory'),
      actions: [
        {
          label: t('common:cancel'),
          variant: 'text',
          onClick: onClose,
        },
      ],
    },
  };

  return (
    <ModalNotification
      fullWidth
      size='sm'
      open
      onClose={onClose}
      dialogProps={{
        title: dialogConfig[step].title,
        content: (
          <>
            {step === 'default' && (
              <OptionalMetadataForm
                optionalMetadata={optionalMetadata}
                optionalFormsList={optionalFormsList}
                handleFieldValuesChange={(fieldValue, idx) =>
                  handleFieldValuesChange(fieldValue, 'optional', idx)
                }
                handleFieldVisibilityChange={handleFieldVisibilityChange}
              />
            )}

            {step === 'confirmation' && (
              <>
                <Typography variant='body1' mb={2}>
                  {t('inventory:reviewUpdateInventoryModal.message')}
                </Typography>

                {transformOptionalFormsList().map((item) => (
                  <Stack key={item.name} direction='row' gap={2}>
                    <Typography variant='body1' fontWeight={500}>
                      {item.name}:
                    </Typography>
                    <Typography variant='body1'>
                      {String(
                        item.value === ''
                          ? 'No value provided'
                          : item.value || item.checked,
                      )}
                    </Typography>
                  </Stack>
                ))}
              </>
            )}

            {step === 'error' && (
              <Stack direction='column' gap={2}>
                {errors?.map((error, index) => (
                  <Alert key={index} severity='error'>
                    {error}
                  </Alert>
                ))}
              </Stack>
            )}
          </>
        ),
        actions: dialogConfig[step]
          .actions as ModalNotificationProps['dialogProps']['actions'],
      }}
    />
  );
};

export default UpdateInventoryItemsDialog;
